// Shared membership types + helpers for the scanner verify panel and the
// owner customer-detail membership section (SLICE-MEMBERSHIP-PASS).
//
// Snake_case mirrors the API responses verbatim (the membership checkin
// endpoints + GET /api/cards/:id `membership_context`) so there's no mapping
// layer to drift.

/** Display status of a membership, resolved server-side from its expiry. */
export type MembershipStatus = 'active' | 'expiring_soon' | 'expired'

/**
 * Response of POST /api/staff/cards/:id/membership/checkin and the owner
 * POST /api/cards/:id/membership/checkin. Drives the scanner verify panel.
 */
// NB: keep in sync with the verify endpoints (apps/api staff/cards + cards).
export interface MembershipCheckinResponse {
  card_id: string
  member_name: string | null
  program_name: string
  tier_name: string | null
  member_number: string
  member_number_label: string
  status: MembershipStatus
  expires_at: string | null
  last_check_in_at: string | null
}

/**
 * The `membership_context` block on GET /api/cards/:id (null for loyalty
 * cards). Drives the owner customer-detail membership section.
 */
export interface MembershipContext {
  tier_name: string | null
  // SLICE-MEMBERSHIP-PASS — live program tier config, drives the owner's
  // tier-change dropdown. tier_options is empty when the program has tiers off.
  tiers_enabled: boolean
  tier_options: string[]
  member_number: string
  member_number_label: string
  benefits: string[]
  status: MembershipStatus
  expires_at: string | null
  check_in_count: number
  last_check_in_at: string | null
}

/** Short human label + tone class for the status pill. */
export function membershipStatusLabel(status: MembershipStatus): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'expiring_soon':
      return 'Expiring soon'
    case 'expired':
      return 'Expired'
  }
}

/**
 * Relative "time ago" for the last-visit line. PH-friendly, coarse-grained
 * (we don't need second-precision for a check-in greeting). Returns null for a
 * missing timestamp so callers can render "First visit".
 */
export function relativeTimeAgo(iso: string | null, now = Date.now()): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const diffMs = now - then
  if (diffMs < 0) return 'just now'
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`
  const years = Math.floor(days / 365)
  return `${years} year${years === 1 ? '' : 's'} ago`
}

/** Absolute PH-local date for an expiry line (e.g. "Jun 12, 2026"). */
export function formatExpiry(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-PH', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
